import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Center,
  Checkbox,
  Flex,
  Icon,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Report } from '@finley-technologies/finley-types';

import {
  ReportTypeGQLType,
  useGetReportsLazyQueryGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { DownloadIconButton } from 'packages/common-frontend/components/IconButton';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useReportDownloadUrl } from 'packages/reports/hooks/useReportDownloadUrl';
import { FiCheck } from 'react-icons/fi';

export type ReportsTableProps = {
  creditFacilityId: string;
  refetch?: boolean;
  setRefetch?: React.Dispatch<React.SetStateAction<boolean>>;
};

const previewLabel =
  'Preview reports are generated for internal review, and are not sent to the capital provider.';

const ReportRow = ({
  report,
  reportName = '',
}: {
  report: {
    id: string;
    reportType: ReportTypeGQLType;
    s3Path: string;
    isPreview: boolean;
    created_at: number;
  };
  reportName?: string;
}) => {
  const { id: reportId, reportType, created_at: createdAt } = report;
  const createdAtStr = commonDate.epochToLocaleDateString(createdAt);
  const _reportName = reportName || _.lowerCase(reportType);
  const toast = useToast();
  const { getReportDownloadUrl, error, loading } = useReportDownloadUrl({
    toast,
    reportId,
    reportName: _reportName,
    isDomainReport: false,
  });
  if (error) {
    throw error;
  }
  return (
    <Tr>
      <Td>{reportName}</Td>
      <Td>{commonUtils.toTitleCase(reportType)}</Td>
      <Td textAlign="center">
        {report.isPreview ? (
          <Icon aria-label="report is preview" as={FiCheck} />
        ) : (
          '-'
        )}
      </Td>
      <Td>{createdAtStr}</Td>
      <Td textAlign="center">
        <DownloadIconButton
          ariaLabel="download report"
          label="Download report"
          isLoading={loading}
          fontSize="xl"
          onClick={() => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: `Clicked download for ${reportName}`,
              level: Sentry.Severity.Info,
            });
            try {
              getReportDownloadUrl();
            } catch (err: any) {
              if (!toast.isActive(errorToastId)) {
                errorToastConfig.title = `Error getting report download for ${reportName}`;
                toast(errorToastConfig);
              }
              throw err;
            }
          }}
        />
      </Td>
    </Tr>
  );
};

export const ReportsTable = ({
  creditFacilityId,
  refetch = false,
  setRefetch,
}: ReportsTableProps) => {
  const borderColor = useColorModeValue('gray.100', 'gray.900');
  const toast = useToast();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [includePreviews, setIncludePreviews] = useState<boolean>(false);

  const [reportTypes, setReportTypes] = useState<ReportTypeGQLType[]>(
    _.values(Report.ReportTypeIterable),
  );
  const { pageLimit, setPageLimit, options } = usePaginationLimit(10, [
    10,
    25,
    50,
  ]);
  const [
    getReports,
    { data: reportsData, loading },
  ] = useGetReportsLazyQueryGQLType({
    fetchPolicy: 'network-only',
  });

  const refetchReports = useCallback(
    (cursor?: { after: string } | { before: string }) => {
      getReports({
        variables: {
          ...cursor,
          creditFacilityId,
          limit: pageLimit,
          startDate: startDate
            ? commonDate.transformToStartOfDay(startDate).getTime()
            : null,
          endDate: endDate
            ? commonDate.transformToEndOfDay(endDate).getTime()
            : null,
          reportTypes,
          isPreview: includePreviews ? undefined : false,
        },
      });
    },
    [
      getReports,
      creditFacilityId,
      pageLimit,
      startDate,
      endDate,
      reportTypes,
      includePreviews,
    ],
  );

  useEffect(() => {
    refetchReports();
    if (refetch && setRefetch) {
      setRefetch(false);
    }
  }, [
    getReports,
    setRefetch,
    refetchReports,
    creditFacilityId,
    pageLimit,
    refetch,
    endDate,
    startDate,
    includePreviews,
  ]);

  if (!reportsData || loading) {
    return <Skeleton data-testid="loading-reports-view" height="600px" />;
  }

  const {
    reports: {
      nodes: reports,
      pageInfo: { hasPreviousPage, hasNextPage, startCursor, endCursor },
    },
  } = reportsData;

  return (
    <PaginationControl
      pageLimit={pageLimit}
      setPageLimit={setPageLimit}
      options={options}
      limitLabel="Reports per page"
      hasPreviousPage={hasPreviousPage}
      hasNextPage={hasNextPage}
      onPrevious={() =>
        refetchReports(startCursor ? { before: startCursor } : undefined)
      }
      onNext={() =>
        refetchReports(endCursor ? { after: endCursor } : undefined)
      }
      nextLabel="Older"
      previousLabel="Newer"
      filters={
        <Flex gap="1rem" flexWrap="wrap" marginBottom="1rem" width="100%">
          <IsolatedMultiSelect
            applyButtonText="Apply filters"
            buttonProps={{ minWidth: '250px' }}
            counterLabel="Report types"
            initialSelections={reportTypes}
            isSearchable
            onApplyOptions={setReportTypes}
            optionsHeader="Report types"
            options={Report.ReportTypeIterable.map((reportType) => ({
              label: commonUtils.toSentenceCase(reportType),
              value: reportType,
            }))}
            placeholderText="Select report types"
            showSelectedCount
            titleText="All report types"
          />
          <Flex alignItems="center" flexShrink={0} gap="1rem">
            <Flex
              as="label"
              alignItems="center"
              gap="0.5rem"
              id="include-previews-label"
              flexShrink={0}
            >
              Include Previews
              <Tooltip hasArrow label={previewLabel}>
                <InfoOutlineIcon ml="2px" />
              </Tooltip>
            </Flex>
            <Checkbox
              aria-labelledby="include-previews-label"
              aria-label="include previews"
              isChecked={includePreviews}
              onChange={() => setIncludePreviews(!includePreviews)}
            />
          </Flex>
          <FilterDatePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            FlexProps={{
              flexShrink: 0,
              minWidth: 'fit-content',
              width: '100%',
            }}
          />
        </Flex>
      }
    >
      {reports && !_.isEmpty(reports) ? (
        <Flex
          width="100%"
          overflowX="auto"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Table>
            <Thead>
              <Tr>
                <Th minWidth="20rem">Name</Th>
                <Th minWidth="15rem">Report Type</Th>
                <Th minWidth="8rem" textAlign="center">
                  <Center>
                    Preview
                    <Tooltip hasArrow label={previewLabel}>
                      <InfoOutlineIcon ml="2px" />
                    </Tooltip>
                  </Center>
                </Th>
                <Th minWidth="10rem">Created</Th>
                <Th minWidth="6rem" textAlign="center">
                  Download
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {reports.map((report) => {
                const reportName = _.last(report.s3Path.split('/'));
                return (
                  <Sentry.ErrorBoundary
                    key={report.id}
                    onError={() => {
                      if (!toast.isActive(errorToastId)) {
                        errorToastConfig.title = `Error in showing report ${reportName}`;
                        toast(errorToastConfig);
                      }
                    }}
                  >
                    <ReportRow reportName={reportName} report={report} />
                  </Sentry.ErrorBoundary>
                );
              })}
            </Tbody>
          </Table>
        </Flex>
      ) : (
        <Alert status="info" mt="35px">
          <AlertIcon />
          <AlertDescription>No reports to view at this time</AlertDescription>
        </Alert>
      )}
    </PaginationControl>
  );
};
